import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADDRESS, DELIVERY_COMPANIES, ENUMS } from 'services/consts';

import { getCountryList, getGovernorates, getStateByCountry } from 'stores/globalAddressSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import CountryForm from './CountryForm';
import GovernorateForm from './GovernorateForm';
import StateForm from './StateForm';
import TagChips from './TagChips';

const DeliveryCompanyMapping = ({ mode, isLoading }) => {
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const formMethods = useFormContext();
	const { watch, control, setValue } = formMethods;

	const {
		append,
		remove,
		fields: items
	} = useFieldArray({
		control, // control props comes from useForm (optional: if you are using FormContext)
		name: 'deliveryCompCities' // unique name for your Field Array
	});

	const fetchAutocompleteData = useCallback(async (props, dcIntegrationName, selectedDC) => {
		const response = await axiosPrivate
			.post(DELIVERY_COMPANIES.GET_DC_SETTINGS_LIST_BY_DC_INTEGRATION, {
				dcIntegrationName,
				keyword: selectedDC ? selectedDC : props?.keyword,
				paging: { offset: 0, count: 100 }
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		return response.data?.data;
	}, []);

	// DeliveryCompanies
	const dcMappingsColumns = useMemo(
		() => [
			{
				id: 'deliveryCompany',
				header: 'deliveryCompany',
				required: true,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						value={watch(`deliveryCompCities[${index}].deliveryCompName`)}
						name={`deliveryCompCities[${index}].deliveryCompName`}
						enumName={ENUMS.DELIVERY_COMPANIES}
						setValue={(value) => {
							setValue(`deliveryCompCities[${index}].deliveryCompName`, value ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						disabled={mode === 'view'}
					/>
				)
			},
			{
				id: 'tier',
				header: 'tier',
				// DCCityTier
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						value={watch(`deliveryCompCities[${index}].tier`)}
						name={`deliveryCompCities[${index}].tier`}
						enumName={ENUMS.DC_CITY_TIER}
						setValue={(value) => {
							setValue(`deliveryCompCities[${index}].tier`, value ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						disabled={mode === 'view'}
					/>
				),
				showOnMobile: true
			},
			{
				id: 'city',
				header: 'city',
				required: true,
				renderCell: ({ row: { index } }) => (
					<StringInput
						name={`deliveryCompCities[${index}].deliveryCompCityName`}
						disabled={mode === 'view'}
					/>
				),
				showOnMobile: true
			},
			{
				id: 'hasCoverage',
				header: 'hasCoverage',
				size: 100,
				required: true,
				renderCell: ({ row: { index } }) => (
					<SwitchInput
						key={`hasCoverage-${index}`}
						value={watch('deliveryCompCities')[index].hasCoverage}
						setValue={(e) => {
							setValue(`deliveryCompCities[${index}].hasCoverage`, e.target.checked, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						disabled={mode === 'view'}
						style={{ width: 'min-content' }}
					/>
				),
				showOnMobile: true
			},
			{
				id: 'deliveryCompSettingsId',
				header: 'deliveryCompSettingsId',
				size: 300,
				required: false,
				renderCell: ({ row: { index } }) => (
					<>
						<AutoComplete
							fetchFunction={(data) =>
								fetchAutocompleteData(
									data,
									watch(`deliveryCompCities[${index}].deliveryCompName`),
									watch(`deliveryCompCities[${index}].deliveryCompSettingsId`)
								)
							}
							columns={[
								{ accessor: 'deliveryCompanySettingsId', width: 50 },
								{ accessor: 'companyName', width: 200 }
							]}
							value={watch(`deliveryCompCities[${index}].deliveryCompSettingsId`)}
							name={`deliveryCompCities[${index}].deliveryCompSettingsId`}
							setValue={(value) => {
								setValue(
									`deliveryCompCities[${index}].deliveryCompSettingsId`,
									value?.deliveryCompanySettingsId ?? null,
									{
										shouldValidate: true,
										shouldDirty: true
									}
								);
							}}
							disabled={mode === 'view'}
							selectionLabelAccessor={['deliveryCompanySettingsId', 'companyName']}
						/>
					</>
				),
				showOnMobile: true
			},
			...(mode !== 'view'
				? [
						{
							id: 'deleteRow',
							size: 20,
							showOnMobile: true,
							maxSize: 20
						}
					]
				: [])
		],
		[watch, setValue, mode]
	);

	return (
		<Table
			deletableLastRow
			columns={dcMappingsColumns}
			loading={isLoading}
			data={items}
			showAddNewRowButton={mode !== 'view'}
			onRowDelete={(row) => remove(row.index)}
			onRowAdd={() => {
				append({ deliveryCompanyID: null, tier: null, cityName: null, hasCoverage: true });
			}}
		/>
	);
};

export const CityForm = NiceModal.create((props) => {
	const { mode = 'add', modalName, row, setCity } = props;
	const [tags, setTags] = useState([]);
	const [countries, setCountries] = useState([]);
	const [states, setStates] = useState([]);
	const [governorates, setGovernorates] = useState([]);

	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();
	const [id, setId] = useState(null);
	const { t } = useTranslation();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					countryName: yup.string().nullable().required(),
					countryID: yup.number().nullable(),
					stateID: yup.number().nullable(),
					stateName: yup.string().nullable(),
					governorateID: yup.number().nullable(),
					governorateName: yup.string().nullable(),
					defaultZipCode: yup.string().nullable(),
					deliveryCompCities: yup.array().of(
						yup.object().shape({
							deliveryCompName: yup.string().nullable().required(),
							tier: yup.string().nullable(),
							deliveryCompCityName: yup.string().nullable().required(),
							hasCoverage: yup.boolean().required(),
							deliveryCompSettingsId: yup.number().nullable()
						})
					)
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			name: null,
			countryID: null,
			countryName: null,
			stateID: null,
			stateName: null,
			governorateID: null,
			governorateName: null,
			defaultZipCode: null,
			deliveryCompCities: []
		}
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const watchedCountryName = watch('countryName');
	const watchedStateName = watch('stateName');

	const onSubmit = useCallback(
		async (data) => {
			const { deliveryCompCities = [], ...rest } = data;

			await axios
				.post(ADDRESS.SAVE_CITY, {
					...rest,
					...((row?.id || id) && { id: row?.id || id }),
					deliveryCompanies: deliveryCompCities
				})
				.then((response) => {
					setId(response.data.id);
					setCity && setCity(response.data);
					if (!row?.id && !id) {
						showSuccess({
							message: 'City meta added.'
						});
						if (response.data?.id) {
							handleCloseModal();
						}
					} else {
						showSuccess({
							message: 'City meta updated'
						});
						dispatch(updateDynamicListData(DYNAMICLISTS.CITIES));
						handleCloseModal();
					}
				})
				.catch((error) => {
					if (error?.response?.data?.code === 'error.AD002') {
						showError({
							message: t('error.AD002', {
								value: t('city')
							})
						});
					} else {
						showError(error);
						AppLogger.error(error);
					}
				});
		},
		[row, tags, id]
	);

	const fetchCountries = useCallback(async () => {
		await dispatch(getCountryList())
			.unwrap()
			.then((response) => {
				setCountries(response);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: 'noDataFound'
				});
				setCountries([]);
			});
	}, []);

	const fetchStatesByCountry = useCallback(async (name) => {
		await dispatch(getStateByCountry(name))
			.unwrap()
			.then((response) => {
				setStates(response?.data ?? []);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: 'noDataFound'
				});
				setStates([]);
			});
	}, []);

	const fetchGovernorates = useCallback(
		async (stateName) => {
			await dispatch(
				getGovernorates({
					country: watchedCountryName,
					state: stateName
				})
			)
				.unwrap()
				.then((response) => {
					setGovernorates(response ?? []);
				});
		},
		[watchedCountryName]
	);

	const defaultColSpan = {
		md: 6
	};

	useEffect(() => {
		fetchCountries();
	}, []);

	useEffect(() => {
		const getCityTags = async (id) => {
			await axios
				.post(ADDRESS.GET_CITY_TAG_LIST, {
					parentID: id
				})
				.then((res) => {
					if (Array.isArray(res.data.data)) {
						setTags(res.data.data ?? []);
					} else {
						setTags([]);
					}
				})
				.catch((error) => {
					showError(error);
					AppLogger.error(error);
				});
		};

		const getCityDeliveryCompanies = async (id) =>
			await axios
				.post(ADDRESS.GET_CITY_DELIVERY_COMPANIES, {
					cityID: id
				})
				.then((res) => {
					if (res?.data?.deliveryCompCities?.length) {
						return res.data.deliveryCompCities;
					} else {
						return [];
					}
				})
				.catch((error) => {
					showError(error);
					AppLogger.error(error);
					return [];
				});

		if (row?.id) {
			getCityTags(row.id);

			getCityDeliveryCompanies(row.id).then((deliveryComps) => {
				reset({
					name: row.name ?? null,
					countryID: row.countryID ?? null,
					countryName: row.countryName ?? null,
					stateID: row.stateID ?? null,
					stateName: row.stateName ?? null,
					defaultZipCode: row.defaultZipCode ?? null,
					deliveryCompCities: deliveryComps.map((d) => ({
						deliveryCompName: d.deliveryCompName ?? null,
						tier: d.tier ?? null,
						deliveryCompCityName: d.deliveryCompCityName ?? null,
						hasCoverage: d.hasCoverage ?? false,
						id: d.id ?? null,
						cityID: d.cityID ?? null,
						deliveryCompSettingsId: d.deliveryCompSettingsId ?? null
					}))
				});
			});
		}
	}, [row]);

	useEffect(() => {
		if (mode === 'add' && Object.keys(row ?? {}).length > 0) {
			reset({
				...row
			});
		}
	}, [row, mode]);

	useEffect(() => {
		if (!countries.length) return;
		if (!watchedCountryName) {
			setStates([]);
			setValue('countryID', null);
		} else {
			const country = countries.find((country) => country.shortCode === watchedCountryName);
			fetchStatesByCountry(watchedCountryName);
			setValue('countryID', country?.id ?? null);
		}
	}, [watchedCountryName, countries]);

	useEffect(() => {
		if (!states.length) return;
		if (!watchedStateName) {
			setValue('stateID', null);
		} else {
			const stateID = states.find((state) => state.name === watchedStateName)?.id;
			setValue('stateID', stateID ?? null);
		}
	}, [watchedStateName, states]);

	useEffect(() => {
		if (!states.length) return;
		if (!watchedStateName) {
			setValue('stateID', null);
			setValue('governorateID', null);
			setValue('governorateName', null);
		} else {
			const stateID = states.find((state) => state.name === watchedStateName)?.id;
			if (stateID) {
				setValue('stateID', stateID);
				fetchGovernorates(watchedStateName);
			}
		}
	}, [watchedStateName, states]);

	const watchedGovernorateName = watch('governorateName');

	useEffect(() => {
		if (!governorates.length) return;
		if (!watchedGovernorateName) {
			setValue('governorateID', null);
		} else {
			const governorateID = governorates.find((governorate) => governorate.name === watchedGovernorateName)?.id;
			setValue('governorateID', governorateID ?? null);
		}
	}, [watchedGovernorateName, governorates]);

	return (
		<Modal fullwidth name={modalName} maxWidth="x-large" className="modal-city">
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'add' ? 'addCity' : mode === 'edit' ? 'editCity' : 'viewCity'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="name" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="name" disabled={mode === 'view'} />
					</FormItem>
					{/* Country */}
					<FormItem colSpan={defaultColSpan} label="country" required isLoading={isLoading}>
						<EnumSelect
							name="countryName"
							labelPrefix="country."
							data={countries?.map((country) => country.shortCode) ?? []}
							setValue={(value) => {
								setValue('countryName', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watchedCountryName}
							valueRemovable={false}
							disabled={mode === 'view'}
							firstItem={{
								action: () =>
									NiceModal.show(CountryForm, {
										setCountry: (value) => {
											setValue('countryName', value.shortCode ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('countryID', value.id ?? null);
										}
									}),
								label: <Typography color="var(--blue)">addCountry</Typography>
							}}
						/>
					</FormItem>
					{/* Country */}
					{/* State */}
					<FormItem colSpan={defaultColSpan} label="state" isLoading={isLoading}>
						<EnumSelect
							name="stateName"
							data={states.map((state) => state.name)}
							setValue={(value) => {
								setValue('stateName', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watchedStateName}
							disabled={mode === 'view'}
							firstItem={{
								action: () =>
									NiceModal.show(StateForm, {
										setState: (value) => {
											setValue('stateName', value.name ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('stateID', value.id ?? null);
										}
									}),
								label: <Typography color="var(--blue)">addState</Typography>
							}}
						/>
					</FormItem>
					{/* State */}
					{/* Governorate */}
					<FormItem colSpan={defaultColSpan} label="governorate" isLoading={isLoading}>
						<EnumSelect
							name="governorateName"
							data={governorates.map((governorate) => governorate.name)}
							setValue={(value) => {
								setValue('governorateName', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watchedGovernorateName}
							disabled={mode === 'view'}
							firstItem={{
								action: () =>
									NiceModal.show(GovernorateForm, {
										setGovernorate: (value) => {
											setValue('governorateName', value.name ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('governorateID', value.id ?? null);
										}
									}),
								label: <Typography color="var(--blue)">addGovernorate</Typography>
							}}
						/>
					</FormItem>
					{/* Governorate */}
					{/* DefaultZipCode */}
					<FormItem label="defaultZipCode" colSpan={defaultColSpan} isLoading={isLoading}>
						<StringInput name="defaultZipCode" disabled={mode === 'view'} />
					</FormItem>
					{/* DefaultZipCode */}
					{/* Tags */}
					{mode !== 'add' && (
						<FormItem label="tags" childStyle={{ margin: 0 }} isLoading={isLoading}>
							<TagChips
								tags={tags}
								setTags={setTags}
								parentID={row?.id || id}
								type="city"
								disabled={mode === 'view'}
								isLoading={isLoading}
								autoTranslation={false}
							/>
						</FormItem>
					)}
					{/* Tags */}
					{/* DeliveryCompanies */}
					<FormItem label="deliveryCompanies" isLoading={isLoading} toolTip="deliveryCompaniesMapping">
						<DeliveryCompanyMapping mode={mode} isLoading={isLoading} />
					</FormItem>
					{/* DeliveryCompanies */}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' && (
					<Button
						label={mode === 'add' ? 'add' : 'update'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit)}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default CityForm;
